/**
 * suMisura — le condizioni di uno strumento a canone trattate con UNA azienda (atlante cap. 251, tappa 4; decisione di
 * Simone del 07/10/2026: «come pratica commerciale potrebbe essere necessario proporre prezzi diversi per aziende
 * diverse»).
 *
 * Dove sta: `billing.su_misura.<capability>` sul documento dell'azienda, scritto SOLO dal server
 * (`consoleCondizioniSuMisura`; `billing` è fra i campi che le regole proteggono):
 *   { attuale: Versione | null, storico: Versione[] }
 * Ogni versione porta le sue condizioni e i DUE prezzi Stripe creati per quell'azienda (canone e consumo, sullo stesso
 * prodotto e misuratore del listino). `storico` tiene le versioni tolte o sostituite: un abbonamento che ha ancora quei
 * prezzi va riconosciuto e pagato a quelle condizioni. Uno strumento SOLO a canone (`consumo: null`) ha un prezzo solo:
 * consumo e prezzo del consumo restano nulli anche su misura.
 *
 * Nucleo PURO, condiviso fra gestionale, console e server.
 */

import type { AmbienteStripe, ConsumoDelloStrumento, StrumentoACanone, UnitaDiConsumo } from './listino';

export interface VersioneSuMisura {
  readonly canone_mensile_eur: number;
  /** Null per uno strumento solo a canone. */
  readonly consumo: ConsumoDelloStrumento | null;
  readonly ambiente: AmbienteStripe;
  readonly price_canone_id: string;
  readonly price_consumo_id: string | null;
  readonly dal_ms: number | null;
  readonly fino_al_ms: number | null;
  /** Perché, in parole di chi l'ha deciso («accordo con Luigi del 07/10»). */
  readonly nota: string;
}

export interface SuMisuraDelloStrumento {
  readonly attuale: VersioneSuMisura | null;
  readonly storico: readonly VersioneSuMisura[];
}

const NESSUNA: SuMisuraDelloStrumento = { attuale: null, storico: [] };

function millisecondi(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'object' || v === null) return null;
  const o = v as { toMillis?: unknown; seconds?: unknown; _seconds?: unknown };
  if (typeof o.toMillis === 'function') return (o.toMillis as () => number)();
  const s = typeof o.seconds === 'number' ? o.seconds : typeof o._seconds === 'number' ? o._seconds : null;
  return s === null ? null : s * 1000;
}

const positivoOZero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;
const pieno = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';

/** `unita` null = strumento solo a canone: la versione porta il canone e il suo prezzo, nient'altro. */
function versioneDaDati(v: unknown, unita: UnitaDiConsumo | null): VersioneSuMisura | null {
  if (typeof v !== 'object' || v === null) return null;
  const d = v as Record<string, unknown>;
  if (!positivoOZero(d.canone_mensile_eur)) return null;
  if (d.ambiente !== 'test' && d.ambiente !== 'live') return null;
  if (!pieno(d.price_canone_id)) return null;
  let consumo: ConsumoDelloStrumento | null = null;
  if (unita !== null) {
    const c = (typeof d.consumo === 'object' && d.consumo !== null ? d.consumo : {}) as Record<string, unknown>;
    if (!positivoOZero(c.prezzo_extra_eur)) return null;
    if (!positivoOZero(c.quota_compresa) || !Number.isInteger(c.quota_compresa)) return null;
    if (!pieno(d.price_consumo_id)) return null;
    // L'unità è quella dello strumento: un accordo cambia i numeri, non cosa si conta.
    consumo = { unita, quota_compresa: c.quota_compresa, prezzo_extra_eur: c.prezzo_extra_eur };
  }
  return {
    canone_mensile_eur: d.canone_mensile_eur,
    consumo,
    ambiente: d.ambiente,
    price_canone_id: d.price_canone_id,
    price_consumo_id: unita === null ? null : (d.price_consumo_id as string),
    dal_ms: millisecondi(d.dal),
    fino_al_ms: millisecondi(d.fino_al),
    nota: typeof d.nota === 'string' ? d.nota : '',
  };
}

/** Le condizioni su misura di uno strumento per un'azienda (niente = listino). Una voce illeggibile si scarta. */
export function suMisuraDallAzienda(datiAzienda: unknown, s: StrumentoACanone): SuMisuraDelloStrumento {
  const billing = (datiAzienda as { billing?: { su_misura?: Record<string, unknown> } } | null | undefined)?.billing;
  const voce = billing?.su_misura?.[s.capability] as { attuale?: unknown; storico?: unknown } | undefined;
  if (!voce || typeof voce !== 'object') return NESSUNA;
  const storico = Array.isArray(voce.storico) ? voce.storico : [];
  return {
    attuale: versioneDaDati(voce.attuale, s.consumo?.unita ?? null),
    storico: storico.map((v) => versioneDaDati(v, s.consumo?.unita ?? null)).filter((v): v is VersioneSuMisura => v !== null),
  };
}

/** Tutte le versioni su misura dell'azienda per lo strumento (l'attuale e quelle di prima). */
export function versioniSuMisura(datiAzienda: unknown, s: StrumentoACanone): VersioneSuMisura[] {
  const m = suMisuraDallAzienda(datiAzienda, s);
  return [...(m.attuale ? [m.attuale] : []), ...m.storico];
}

/**
 * I prezzi che l'azienda deve ricevere quando attiva lo strumento in un ambiente: i suoi, se ha condizioni su misura
 * create in quell'ambiente, altrimenti quelli in vendita del listino. Null se lo strumento non ha prezzi lì.
 */
export function prezziDaAttivare(
  datiAzienda: unknown,
  s: StrumentoACanone,
  ambiente: AmbienteStripe,
): { readonly price_canone_id: string; readonly price_consumo_id: string | null; readonly su_misura: boolean } | null {
  const attuale = suMisuraDallAzienda(datiAzienda, s).attuale;
  if (attuale && attuale.ambiente === ambiente) {
    return { price_canone_id: attuale.price_canone_id, price_consumo_id: attuale.price_consumo_id, su_misura: true };
  }
  const ids = s.stripe[ambiente];
  return ids ? { price_canone_id: ids.price_canone_id, price_consumo_id: ids.price_consumo_id, su_misura: false } : null;
}
